import { useSelectedLocation } from "components";
import { useTypedQuery } from "hooks";
import { CommunicationTemplatesQueryKeys } from "modules/CommunicationTemplates/queryKeys";
import { ApiInstance } from "utils/Api";

import type { CommunicationMedium } from "models";
import type { CommunicationMediumVariation, GroupedCommunicationMedium } from "modules/CommunicationTemplates/queryKeys";

const getVariationMapKey = (m: CommunicationMedium) => `${m.event_type ?? m.keylocker_communication_event_type ?? m.acses_communication_event_type}_${m.channel}`;

const groupCommunicationMediums = (mediums: CommunicationMedium[]) => {
  const groupedMediums: GroupedCommunicationMedium[] = [];
  const variationsMap = new Map<string, CommunicationMediumVariation[]>();
  mediums.forEach(m => {
    if (m.car_model || m.car_brand) {
      const key = getVariationMapKey(m);
      const variations = variationsMap.get(key);

      if (variations) variations.push({ ...m, parent_id: m.communication_medium_id });
      else variationsMap.set(key, [{ ...m, parent_id: m.communication_medium_id }]);
    }
  });

  mediums.forEach(m => {
    if (m.car_model || m.car_brand) return;

    groupedMediums.push({ ...m, variations: variationsMap.get(getVariationMapKey(m)) });
  });

  return groupedMediums;
};

const sortCommunicationMediums = (mediums: CommunicationMedium[]) =>
  groupCommunicationMediums(mediums).sort((a, b) => {
    const eventTypeA = a.event_type ?? 0;
    const eventTypeB = b.event_type ?? 0;

    if (eventTypeA > eventTypeB) return 1;
    if (eventTypeA < eventTypeB) return -1;
    if (a.channel > b.channel) return 1;
    if (a.channel < b.channel) return -1;
    return 0;
  });

export const useCommunicationMediumsQuery = () => {
  const { selectedLocation } = useSelectedLocation();

  const query = useTypedQuery({
    queryKey: CommunicationTemplatesQueryKeys.getCommunicationMediums,
    queryFn: async ({ baseUrl, endpoint }) => (await ApiInstance.post(endpoint, { dealer_location_id: selectedLocation?.id }, baseUrl)).data.communication_mediums,
    enabled: false,
    refetchOnDependencies: [selectedLocation],
    select: sortCommunicationMediums
  });

  return query;
};
